import React, { useCallback, useEffect, useId, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, X } from 'lucide-react';
import { SiteSection, SiteSectionHeader } from '../../components/site';
import { PRODUCT_ICON_MAP } from '../product-subpage/productIcons';
import type { ProductSubpageConfig } from '../product-subpage/types';

export const SolutionAdvantages: React.FC<{
  advantages: ProductSubpageConfig['advantages'];
}> = ({ advantages }) => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const sectionId = useId();

  const closeModal = useCallback(() => setOpenIndex(null), []);
  const openModal = useCallback((index: number) => setOpenIndex(index), []);

  const handleClosePointerDown = useCallback(
    (event: React.PointerEvent<HTMLButtonElement>) => {
      event.preventDefault();
      event.stopPropagation();
      closeModal();
    },
    [closeModal],
  );

  useEffect(() => {
    if (openIndex === null) return undefined;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeModal();
    };

    document.addEventListener('keydown', onKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = '';
    };
  }, [closeModal, openIndex]);

  const hasHighlights = advantages.items.some((item) => (item.highlights?.length ?? 0) > 0);
  const hasIotShowcase = (advantages.kpis?.length ?? 0) > 0;
  const openItem = openIndex !== null ? advantages.items[openIndex] : null;
  const openHighlights = openItem?.highlights ?? [];

  return (
    <SiteSection
      id="advantages"
      tone="muted"
      className={`platform-sub-advantages platform-sub-advantages--premium${
        hasHighlights ? ' platform-sub-advantages--interactive' : ''
      }${hasIotShowcase ? ' platform-sub-advantages--iot-showcase' : ''}`}
    >
      {hasIotShowcase ? (
        <div className="platform-sub-advantages__decor" aria-hidden>
          <svg className="platform-sub-advantages__decor-icon platform-sub-advantages__decor-icon--wifi" viewBox="0 0 24 24" fill="none">
            <path d="M2.5 8.5a14 14 0 0 1 19 0" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            <path d="M5.5 11.5a10 10 0 0 1 13 0" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            <path d="M8.5 14.5a6 6 0 0 1 7 0" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            <circle cx="12" cy="18" r="1.25" fill="currentColor" />
          </svg>
          <span className="platform-sub-advantages__decor-dots">
            <span />
            <span />
            <span />
          </span>
          <svg className="platform-sub-advantages__decor-icon platform-sub-advantages__decor-icon--graph" viewBox="0 0 24 24" fill="none">
            <path d="M4 18V6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            <path d="M4 18h16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            <path d="M8 14l3-4 3 2 4-6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <svg className="platform-sub-advantages__decor-icon platform-sub-advantages__decor-icon--cloud" viewBox="0 0 24 24" fill="none">
            <path
              d="M7 18h10a4 4 0 0 0 .4-8 5.5 5.5 0 0 0-10.6 1.8A3.5 3.5 0 0 0 7 18Z"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinejoin="round"
            />
            <path d="M12 11v4M10.5 13h3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
          <svg className="platform-sub-advantages__decor-icon platform-sub-advantages__decor-icon--lock" viewBox="0 0 24 24" fill="none">
            <rect x="6" y="10" width="12" height="10" rx="2" stroke="currentColor" strokeWidth="1.5" />
            <path d="M8.5 10V8a3.5 3.5 0 0 1 7 0v2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            <circle cx="12" cy="15" r="1.25" fill="currentColor" />
          </svg>
        </div>
      ) : null}
      <SiteSectionHeader
        eyebrow={advantages.eyebrow}
        title={advantages.title}
        subtitle={advantages.subtitle}
        align="center"
      />
      {advantages.kpis?.length ? (
        <ul className="platform-sub-advantages__kpis" aria-label="Platform highlights">
          {advantages.kpis.map((kpi) => {
            const KpiIcon = PRODUCT_ICON_MAP[kpi.icon];
            return (
              <li key={kpi.value} className="platform-sub-advantages__kpi">
                <span className="platform-sub-advantages__kpi-icon" aria-hidden>
                  <KpiIcon size={18} strokeWidth={1.75} />
                </span>
                <span className="platform-sub-advantages__kpi-value">{kpi.value}</span>
              </li>
            );
          })}
        </ul>
      ) : null}
      <ul className="platform-sub-advantages__grid">
        {advantages.items.map((item, index) => {
          const Icon = PRODUCT_ICON_MAP[item.icon];
          const highlights = item.highlights ?? [];
          const isInteractive = highlights.length > 0;
          const modalId = `${sectionId}-advantage-${index}`;

          return (
            <li
              key={item.title}
              className={`platform-sub-advantage-card${
                isInteractive ? ' platform-sub-advantage-card--interactive' : ''
              }${openIndex === index ? ' is-modal-open' : ''}`}
            >
              <div className="platform-sub-advantage-card__surface">
                <span className="platform-sub-advantage-card__icon" aria-hidden>
                  <Icon size={hasIotShowcase ? 28 : 22} strokeWidth={1.75} />
                </span>
                <h3 className="platform-sub-advantage-card__title">{item.title}</h3>
                <p className="platform-sub-advantage-card__desc">{item.description}</p>
                {item.metricBadge ? (
                  <span className="platform-sub-advantage-card__metric">{item.metricBadge}</span>
                ) : null}
                {isInteractive ? (
                  <button
                    type="button"
                    className="platform-sub-advantage-card__explore"
                    aria-expanded={openIndex === index}
                    aria-controls={modalId}
                    onClick={() => openModal(index)}
                  >
                    Explore more
                  </button>
                ) : null}
              </div>
            </li>
          );
        })}
      </ul>

      {openItem && openHighlights.length > 0
        ? createPortal(
            <div className="platform-sub-advantage-modal">
              <button
                type="button"
                className="platform-sub-advantage-modal__backdrop"
                aria-label="Close details"
                onPointerDown={handleClosePointerDown}
              />
              <div
                id={`${sectionId}-advantage-${openIndex}`}
                className="platform-sub-advantage-modal__dialog"
                role="dialog"
                aria-modal="true"
                aria-labelledby={`${sectionId}-advantage-title-${openIndex}`}
                onPointerDown={(event) => event.stopPropagation()}
              >
                <button
                  type="button"
                  className="platform-sub-advantage-modal__close"
                  aria-label="Close"
                  onPointerDown={handleClosePointerDown}
                >
                  <X size={20} strokeWidth={2} />
                </button>

                {openItem.image ? (
                  <div className="platform-sub-advantage-modal__media">
                    <img
                      src={openItem.image.src}
                      alt={openItem.image.alt}
                      width={openItem.image.width}
                      height={openItem.image.height}
                      loading="lazy"
                      decoding="async"
                    />
                  </div>
                ) : null}

                <h3
                  id={`${sectionId}-advantage-title-${openIndex}`}
                  className="platform-sub-advantage-modal__title"
                >
                  {openItem.title}
                </h3>

                <p className="platform-sub-advantage-modal__desc">{openItem.description}</p>

                <ul className="platform-sub-advantage-modal__list">
                  {openHighlights.map((highlight) => (
                    <li key={highlight}>
                      <span className="platform-sub-advantage-modal__check" aria-hidden>
                        <Check size={15} strokeWidth={2.5} />
                      </span>
                      <span>{highlight}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>,
            document.body,
          )
        : null}
    </SiteSection>
  );
};
